import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import SearchableCheckboxList from 'v2/components/SearchableCheckboxList/SearchableCheckboxList';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';

const FORM_ID = 'device-group-user-groups-step-form';
const STEP_INDEX = 1;

interface FormValues {
  userGroupIds: string[];
}

interface Props {
  groupId: string | null;
  allVisitorGroups: VisitorGroup[];
  assignUserGroupsToDeviceGroup: (
    groupId: string,
    visitorGroupIds: string[]
  ) => Promise<{ success: boolean; errorMessage?: string }>;
}

const DeviceGroupUserGroupsStep: React.FC<Props> = ({ groupId, allVisitorGroups, assignUserGroupsToDeviceGroup }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();
  const [isLoading, setIsLoading] = React.useState(false);

  const { handleSubmit, control } = useForm<FormValues>({ mode: 'onTouched', defaultValues: { userGroupIds: [] } });

  const onSubmit = handleSubmit(async (data) => {
    if (!groupId) return;
    setIsLoading(true);
    try {
      if (data.userGroupIds.length > 0) {
        const result = await assignUserGroupsToDeviceGroup(groupId, data.userGroupIds);
        if (!result.success) {
          showSnackbar(result.errorMessage ?? translation.an_error_occurred, { severity: 'error' });
          return;
        }
      }
      goToNext();
    } finally {
      setIsLoading(false);
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="device-group-user-groups-step-submit"
          id="device-group-user-groups-next-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.next}
        </Button>
      </Box>
    );
  }, [isLoading, setFooterContent, steps, translation]);

  return (
    <form id={FORM_ID} onSubmit={onSubmit} noValidate>
      <Controller
        name="userGroupIds"
        control={control}
        render={({ field }) => (
          <SearchableCheckboxList
            items={allVisitorGroups}
            selectedIds={field.value}
            onChange={field.onChange}
            idPrefix="user-groups-"
          />
        )}
      />
    </form>
  );
};

export default DeviceGroupUserGroupsStep;
